import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import {
  cleanerError,
  formatStock,
  normalizeDecimalInput,
  parseMoneyToCents,
} from "@/lib/utils";
import { useAdjustStockMutation } from "@/redux/services/inventory/ingredient.api";
import {
  Ingredient,
  StockAdjustmentReason,
} from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

const DECIMAL_REGEX = /^\d+(\.\d{1,6})?$/;
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

export interface StockAdjustmentFormData {
  reason: StockAdjustmentReason;
  packaging?: string;
  quantity?: string;
  newCostPerUnit?: string;
  notes?: string;
}

export const stockAdjustmentValidationSchema: yup.ObjectSchema<StockAdjustmentFormData> =
  yup.object().shape({
    reason: yup
      .mixed<StockAdjustmentReason>()
      .oneOf(["delivery", "waste", "correction", "return", "cost_correction"])
      .required()
      .label("Raison"),
    quantity: yup
      .string()
      .when("reason", {
        is: "cost_correction",
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema
            .required()
            .test(
              "is-positive-decimal",
              "La quantité doit être un nombre positif (max 6 décimales)",
              (value) => {
                // "1,5" (French keypad) is read as 1.5, not rejected.
                const normalized = normalizeDecimalInput(value ?? "");
                return (
                  normalized !== null &&
                  DECIMAL_REGEX.test(normalized) &&
                  parseFloat(normalized) > 0
                );
              }
            ),
      })
      .label("Quantité"),
    newCostPerUnit: yup
      .string()
      .when("reason", {
        is: "cost_correction",
        then: (schema) =>
          schema
            .required()
            .test(
              "is-non-negative-price",
              "Le coût doit être un nombre positif (max 2 décimales)",
              (value) =>
                PRICE_REGEX.test(normalizeDecimalInput(value ?? "") ?? "")
            ),
        otherwise: (schema) => schema.optional(),
      })
      .label("Nouveau coût par unité"),
    packaging: yup.string().optional().label("Conditionnement"),
    notes: yup.string().optional().max(500).label("Notes"),
  });

const applyReasonSign = (
  current: number,
  qty: number,
  reason: StockAdjustmentReason
) => {
  switch (reason) {
    case "delivery":
    case "return":
    case "correction":
      return current + qty;
    case "waste":
    case "order_deduction":
    case "cancellation":
      return current - qty;
    case "cost_correction":
      return current;
    default:
      return current;
  }
};

export const useStockAdjustment = (
  closeModal: () => void,
  ingredient: Ingredient
) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
    watch,
  } = useForm<StockAdjustmentFormData>({
    resolver: yupResolver(stockAdjustmentValidationSchema),
    defaultValues: {
      reason: "delivery",
      packaging: ingredient.packagings?.find((p) => p.isDefault)?.id ?? "",
      quantity: "",
      newCostPerUnit: "",
      notes: "",
    },
  });

  const [adjustStock, { isLoading }] = useAdjustStockMutation();

  const reason = watch("reason");
  const quantity = watch("quantity");
  const packagingId = watch("packaging");
  const selectedPackaging = ingredient.packagings?.find(
    (p) => p.id === packagingId
  );
  const current = parseFloat(ingredient.currentStock) || 0;
  const rawQty = parseFloat(normalizeDecimalInput(quantity ?? "") ?? "") || 0;
  const baseQty = selectedPackaging
    ? rawQty * (parseFloat(selectedPackaging.quantityInBase) || 0)
    : rawQty;
  const newStock = applyReasonSign(current, baseQty, reason);

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const onSubmit = async (data: StockAdjustmentFormData) => {
    const isCostCorrection = data.reason === "cost_correction";
    const trimmedNotes = data.notes?.trim();
    const normalizedQuantity = normalizeDecimalInput(data.quantity ?? "") ?? "";

    const payload = isCostCorrection
      ? {
          reason: data.reason,
          newCostPerUnit: parseMoneyToCents(data.newCostPerUnit) ?? 0,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        }
      : data.packaging
      ? {
          reason: data.reason,
          packaging: `/api/ingredient_packagings/${data.packaging}`,
          packageQuantity: normalizedQuantity,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        }
      : {
          reason: data.reason,
          quantity: normalizedQuantity,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        };

    const res = await adjustStock({
      ingredientUuid: ingredient.id,
      data: payload,
    });

    if ("data" in res) {
      toast.success(
        isCostCorrection
          ? "Coût par unité mis à jour"
          : `Stock mis à jour : ${formatStock(String(newStock), ingredient.baseUnit)}`
      );
      reset();
      closeModal();
    } else if ("error" in res) {
      toastApiError(res.error, "Erreur lors de l'ajustement du stock");
    }
  };

  return {
    register,
    errors,
    setValue,
    watch,
    reason,
    quantity,
    selectedPackaging,
    baseQty,
    newStock,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
